import { createReducer, on } from '@ngrx/store';
import {
    isIdemSale,
    updateChild,
} from 'shared/ui/tables/table-grouped-by-category/entityWithChildrenUtils';
import * as TotalGrossActions from '../total-gross/total-gross.actions';
import * as VipUpgradesActions from './vip-upgrades.actions';
import { VipUpgrade } from './vip-upgrades.models';

// ----------------------------------------------------------------------
// @ States
// ----------------------------------------------------------------------

export interface VipUpgradesState {
    vipUpgrades: VipUpgrade[];
    vipUpgrade: VipUpgrade | null;
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial States
// ----------------------------------------------------------------------

export const initialVipUpgradesState: VipUpgradesState = {
    vipUpgrades: [],
    vipUpgrade: null,
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducers
// ----------------------------------------------------------------------

// All VipUpgrades Reducer
export const vipUpgradesReducer = createReducer(
    initialVipUpgradesState,

    // On Load All
    on(VipUpgradesActions.loadVipUpgrades, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(VipUpgradesActions.loadVipUpgradesSuccess, (state, { vipUpgrades }) => ({
        ...state,
        isLoading: false,
        vipUpgrades: vipUpgrades,
    })),
    on(VipUpgradesActions.loadVipUpgradesFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Clear
    on(VipUpgradesActions.clearVipUpgrades, (state) => ({
        ...state,
        vipUpgrades: [],
        vipUpgrade: null,
        isLoading: false,
        error: null,
    })),

    // On Load Single
    on(VipUpgradesActions.loadVipUpgrade, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(VipUpgradesActions.loadVipUpgradeSuccess, (state, { vipUpgrade }) => ({
        ...state,
        isLoading: false,
        vipUpgrade: vipUpgrade,
    })),
    on(VipUpgradesActions.loadVipUpgradeFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Create
    on(VipUpgradesActions.createVipUpgrade, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(VipUpgradesActions.createVipUpgradeSuccess, (state, { vipUpgrade }) => ({
        ...state,
        isLoading: false,
        vipUpgrades: ((): VipUpgrade[] => {
            const sales = state.vipUpgrades || [];
            let updateSales = [];
            sales.some((sale) => sale.salesRep.id === vipUpgrade.salesRep.id)
                ? (updateSales = sales.map((sale: VipUpgrade) =>
                      sale.salesRep.id === vipUpgrade.salesRep.id
                          ? {
                                // Spread the old sale to keep old cash
                                // and credit card
                                ...sale,
                                children: [
                                    ...sale.children,
                                    ...vipUpgrade.children,
                                ],
                            }
                          : sale
                  ))
                : (updateSales = [...sales, vipUpgrade]);

            return updateSales;
        })(),
    })),
    on(VipUpgradesActions.createVipUpgradeFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Update
    on(VipUpgradesActions.updateVipUpgrade, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(VipUpgradesActions.updateVipUpgradeSuccess, (state, { vipUpgrade }) => ({
        ...state,
        isLoading: false,
        vipUpgrades: state.vipUpgrades.map((sale: VipUpgrade) =>
            isIdemSale(sale, vipUpgrade)
                ? {
                      ...vipUpgrade,
                      children: updateChild(sale, vipUpgrade),
                  }
                : sale
        ),
    })),
    on(VipUpgradesActions.updateVipUpgradeFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete
    on(VipUpgradesActions.deleteVipUpgrade, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        VipUpgradesActions.deleteVipUpgradeSuccess,
        (state, { deletedSales }) => ({
            ...state,
            isLoading: false,
            vipUpgrades: state.vipUpgrades.filter((sales: VipUpgrade) =>
                sales.children.every(
                    (child) => child.saleId !== deletedSales[0]
                )
            ),
        })
    ),
    on(VipUpgradesActions.deleteVipUpgradeFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On Delete Item
    on(VipUpgradesActions.deleteVipUpgradeItem, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        VipUpgradesActions.deleteVipUpgradeItemSuccess,
        (state, { deletedSaleItem }) => ({
            ...state,
            isLoading: false,
            vipUpgrades: state.vipUpgrades.map((sales: VipUpgrade) => {
                if (
                    sales.children.some(
                        (child) => child.saleId === deletedSaleItem
                    )
                ) {
                    return {
                        ...sales,
                        children: sales.children.filter(
                            (child) => child.saleId !== deletedSaleItem
                        ),
                    };
                } else {
                    return sales;
                }
            }),
        })
    ),
    on(VipUpgradesActions.deleteVipUpgradeItemFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        error: error,
    })),

    // On update total gross
    on(
        TotalGrossActions.updateTotalGrossBySalesRepSuccess,
        (state, { totalGross }) => ({
            ...state,
            isLoading: false,
            vipUpgrades: state.vipUpgrades.map((sales: VipUpgrade) =>
                sales.salesRep.id === totalGross.sales_rep_id
                    ? {
                          ...sales,
                          cash: totalGross.cash,
                          credit_card: totalGross.credit_card,
                          totals: {
                              commission_rate: totalGross.commission_rate,
                              commission_amount: totalGross.commission_amount,
                          },
                          totals_local: {
                              cash: totalGross.total_local.cash,
                              credit_card: totalGross.total_local.credit_card,
                              count: sales.totals_local.count,
                              amount: sales.totals_local.amount,
                          },
                      }
                    : sales
            ),
        })
    )
);
